import { useState } from 'react';
import { Link } from 'react-router';
import { Button, InlineError } from '../../components/ui';
import { PropertyCard, Property } from '../profiles/ProfileProperty';
import { ProspectTierChip } from './ProspectTierChip';
import { linkedinDate } from './JourneyStrip';
import { leadMilestoneError, useRecordLeadMilestone } from './linkedinJourney';
import type { DealJourney, LeadJourney, LeadMilestone } from './linkedinJourney';

const MILESTONES = [
  { kind: 'call_held', label: 'Call held', column: 'call_held_at' },
  { kind: 'no_show', label: 'No show', column: 'no_show_at' },
  { kind: 'proposal_sent', label: 'Proposal sent', column: 'proposal_sent_at' },
] as const;
export function StartedOnLinkedIn({
  row,
  leadId,
  collapsed,
  onToggle,
}: {
  row: LeadJourney | DealJourney | null | undefined;
  leadId: string | null;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  // The keyed body resets pending/error state when a different record opens.
  if (!row?.prospect_id) return null;
  return (
    <PropertyCard title="Started on LinkedIn" collapsed={collapsed} onToggle={onToggle}>
      <OriginBody key={`${row.prospect_id}-${leadId}`} row={row} leadId={leadId} />
    </PropertyCard>
  );
}
function OriginBody({ row, leadId }: { row: LeadJourney | DealJourney; leadId: string | null }) {
  const log = useRecordLeadMilestone();
  const [error, setError] = useState('');
  const [ineligible, setIneligible] = useState(false);
  const [logged, setLogged] = useState<LeadMilestone[]>([]);
  return (
    <>
      <ProspectTierChip tier={row.tier} />
      <Property
        label="First message"
        value={<>{row.first_dm_copy_set_name ?? 'Message not recorded'}</>}
      />
      <Property
        label="Prospect source"
        value={
          <>
            {row.prospect_source === 'senku_daily'
              ? 'Senku daily'
              : row.prospect_source === 'manual'
                ? 'Added by hand'
                : (row.prospect_source?.replaceAll('_', ' ') ?? 'Not recorded')}
          </>
        }
      />
      {row.batch_id && <Property label="Batch" value={<>{row.batch_id}</>} />}
      <Property
        label="First DM"
        value={<>{row.first_dm_at ? linkedinDate(row.first_dm_at) : 'Not recorded'}</>}
      />
      <Property label="DM count" value={<>{row.dm_count ?? 0}</>} />
      <Link className="tracker-lead-link" to={`/tracker?tab=linkedin&prospect=${row.prospect_id}`}>
        Open prospect
      </Link>
      {error && <InlineError>{error}</InlineError>}
      {leadId && !ineligible && (
        <div className="linkedin-milestones">
          {MILESTONES.map(({ kind, label, column }) => {
            const date = row[column];
            return (
              <Button
                key={kind}
                size="sm"
                variant="secondary"
                disabled={date !== null || logged.includes(kind) || log.isPending}
                onClick={() => {
                  setError('');
                  log.mutate(
                    { leadId, kind },
                    {
                      onSuccess: () => setLogged((current) => [...current, kind]),
                      onError: (cause) => {
                        setError(leadMilestoneError(cause.message));
                        if (cause.message.includes('lead_not_from_linkedin')) setIneligible(true);
                      },
                    },
                  );
                }}
              >
                {label}
                {date ? ` · ${linkedinDate(date)}` : logged.includes(kind) ? ' · Logged' : ''}
              </Button>
            );
          })}
        </div>
      )}
    </>
  );
}
